import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import webSocketService from 'services/WebSocketService';

const SocketContext = createContext(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const role = useSelector(state => state.auth.role);

  useEffect(() => {
    // Agent session: JWT is in httpOnly cookie — STOMP connects with cookie on handshake (optional Bearer)
    if (role === 'agent') {
      const client = webSocketService.connect(null);

      if (!client) {
        console.error('Failed to initialize WebSocket connection - no client returned');
        setIsConnected(false);
        return;
      }

      // Listen for connection events
      webSocketService.on('connect', () => {
        console.log('✅ WebSocket connected in context');
        setIsConnected(true);
      });

      webSocketService.on('disconnect', () => {
        console.log('❌ WebSocket disconnected in context');
        setIsConnected(false);
      });

      webSocketService.on('error', (error) => {
        console.error('❌ WebSocket error in context:', error);
        setIsConnected(false);
      });

      // Cleanup on unmount or token change
      return () => {
        console.log('🧹 Cleaning up WebSocket connection');
        webSocketService.disconnect();
        setIsConnected(false);
      };
    } else {
      // Disconnect if not agent or no token
      if (webSocketService && typeof webSocketService.isConnected === 'function' && webSocketService.isConnected()) {
        webSocketService.disconnect();
        setIsConnected(false);
      }
    }
  }, [role]);

  return (
    <SocketContext.Provider value={{ webSocketService, isConnected }}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;
